import { itemDef } from "@eldrun/content";
import { PLACEMENT } from "@eldrun/sim";
import { type Color, Mesh, type Scene, type ShaderMaterial, type Vector3Like } from "three";
import { models, type ModelLibrary } from "../render/model-library.ts";
import { structureGeometry, structureLid } from "../render/structure-meshes.ts";

/** Distância máxima do raio de mira (a câmera em TPV fica ~3,5 m atrás do corpo). */
export const AIM_DISTANCE = 9;
const AIM_STEP = 0.1;

/** Primeiro ponto em que o raio da câmera encontra o terreno (marcha simples), ou undefined. */
export function aimAtGround(origin: Vector3Like, dir: Vector3Like, height: (x: number, z: number) => number, max = AIM_DISTANCE): { x: number; z: number } | undefined {
  for (let t = AIM_STEP; t <= max; t += AIM_STEP) {
    const x = origin.x + dir.x * t;
    const y = origin.y + dir.y * t;
    const z = origin.z + dir.z * t;
    if (y <= height(x, z)) {
      // Refina entre o passo anterior e este (bisseção curta).
      let lo = t - AIM_STEP;
      let hi = t;
      for (let i = 0; i < 6; i++) {
        const mid = (lo + hi) / 2;
        const my = origin.y + dir.y * mid;
        if (my <= height(origin.x + dir.x * mid, origin.z + dir.z * mid)) hi = mid;
        else lo = mid;
      }
      return { x: origin.x + dir.x * hi, z: origin.z + dir.z * hi };
    }
  }
  return undefined;
}

/** Distância do fantasma quando a mira não acha chão ao alcance (olhando o horizonte, por ex.). */
export const PLACE_DISTANCE = 2.5;

/**
 * Onde o fantasma fica: no chão sob a mira, se estiver ao alcance do corpo; senão, um passo à
 * frente do corpo na direção do olhar (como "segurar" a estação na frente).
 */
export function placementTarget(
  origin: Vector3Like,
  dir: Vector3Like,
  body: { x: number; z: number; yaw: number },
  height: (x: number, z: number) => number,
): { x: number; z: number } {
  const hit = aimAtGround(origin, dir, height);
  if (hit && (hit.x - body.x) ** 2 + (hit.z - body.z) ** 2 <= PLACEMENT.reach ** 2) return hit;
  return { x: body.x - Math.sin(body.yaw) * PLACE_DISTANCE, z: body.z - Math.cos(body.yaw) * PLACE_DISTANCE };
}

/** O item na mão se posiciona como estação? */
export const isPlaceableStation = (itemId: string): boolean => {
  const def = itemDef(itemId);
  return def?.kind === "estacao" && Boolean(def.placeable);
};

/** Quarto de volta por toque em R; o fantasma segue o olhar mais esse giro. */
export const ROTATE_STEP = Math.PI / 2;

export function wrapYaw(yaw: number): number {
  return yaw - 2 * Math.PI * Math.floor((yaw + Math.PI) / (2 * Math.PI));
}

/**
 * Fantasma da estação ou da peça na mira: na cor do slot se pode, hachurado se não (F75; o servidor decide de verdade).
 * Com os modelos (F48), a malha do modelo e, no baú, a tampa fechada.
 */
export class PlacementGhost {
  private readonly scene: Scene;
  private readonly material: ShaderMaterial;
  private readonly library: ModelLibrary;
  private mesh: Mesh | undefined;
  private itemId = "";

  constructor(scene: Scene, material: ShaderMaterial, library: ModelLibrary = models.library) {
    this.scene = scene;
    this.material = material;
    this.library = library;
  }

  show(itemId: string, x: number, y: number, z: number, yaw: number, ok: boolean): void {
    if (itemId !== this.itemId) {
      this.hide();
      this.mesh = new Mesh(structureGeometry(itemId, this.library), this.material);
      this.mesh.renderOrder = 10;
      const lid = structureLid(itemId, this.library);
      if (lid) {
        const part = new Mesh(lid.geometry, this.material);
        part.name = "tampa";
        part.renderOrder = 10;
        part.position.fromArray(lid.hinge);
        this.mesh.add(part);
      }
      this.itemId = itemId;
      this.scene.add(this.mesh);
    }
    const mesh = this.mesh as Mesh;
    mesh.visible = true;
    mesh.position.set(x, y, z);
    mesh.rotation.y = yaw;
    this.material.uniforms["uBlocked"]!.value = ok ? 0 : 1;
  }

  /** Cor do slot do jogador (já pelo modo daltônico). */
  setColor(color: string): void {
    (this.material.uniforms["uTint"]!.value as Color).set(color);
  }

  hide(): void {
    if (this.mesh) this.scene.remove(this.mesh);
    this.mesh = undefined;
    this.itemId = "";
  }

  get visible(): boolean {
    return this.mesh?.visible === true;
  }

  dispose(): void {
    this.hide();
    this.material.dispose();
  }
}
